import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import React, { useCallback, useEffect, useState } from 'react'

import {
  useCreateAccountNoteMutation,
  useLazyGetAllAccountNotesQuery,
  usePatchAccountNoteMutation,
  useUpdateAccountNoteMutation,
} from 'api/notes/accountNotesCrud'
import HHAccordion from 'components/common/HHAccordion'
import { getArchivedAndUnarchivedNotes, getSortedNotes } from 'data/notes/notesSelectors'
import T from 'T'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'

import ViewArchivedNotesAction from '../common/actions/ViewArchivedNotesAction'
import AddUpdateNoteDialog from '../common/AddUpdateNoteDialog'
import NoteSkeleton from '../common/NoteSkeleton'
import ViewArchiveNotesDialog from '../common/ViewArchiveNotesDialog'

import AccountNotes from './AccountNotes'

type AccountNotesAccordionVariantProps = {
  accountId?: string
}

const AccountNotesAccordionVariant = ({ accountId = '' }: AccountNotesAccordionVariantProps) => {
  const [getAllAccountNotes, { isFetching: isGetAllNotesLoading, data: notesData }] = useLazyGetAllAccountNotesQuery()
  const [isOpenAddUpdateNoteDialog, setIsOpenAddUpdateNoteDialog] = useState(false)
  const [isOpenViewArchiveNoteDialog, setIsOpenViewArchiveNoteDialog] = useState(false)

  const sortedNotes = getSortedNotes({ notes: get(notesData, 'notes', []) })
  const { unarchivedNotes: notes, archivedNotes } = getArchivedAndUnarchivedNotes({ notes: sortedNotes })

  const handleOpenAddAccountNote = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    setIsOpenAddUpdateNoteDialog(true)
  }, [])

  const handleViewArchiveClick = useCallback(() => setIsOpenViewArchiveNoteDialog(true), [])

  const handleCloseViewArchiveDialog = useCallback(() => setIsOpenViewArchiveNoteDialog(false), [])

  const handleCloseAddAccountNote = useCallback(() => {
    setIsOpenAddUpdateNoteDialog(false)
  }, [])

  useEffect(() => {
    if (accountId) {
      getAllAccountNotes({ id: accountId }).unwrap().catch(handleError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId])

  return (
    <>
      <HHAccordion
        isExpandCollapseHeightSame
        isExpanded
        showCustomActions
        withNewColors
        customActionContent={
          <Box alignItems="center" display="flex">
            <IconButton onClick={handleOpenAddAccountNote}>
              <AddIcon />
            </IconButton>
            <ViewArchivedNotesAction archivedNotes={archivedNotes} onViewArchiveClick={handleViewArchiveClick} />
          </Box>
        }
        showActionIcon={false}
        summary={`${T.ACCOUNT_NOTE}s`}
      >
        {isGetAllNotesLoading && <NoteSkeleton rows={3} />}
        {!isGetAllNotesLoading && (
          // @ts-expect-error AccountNotes is an untyped JS component
          <AccountNotes accountId={accountId} notes={notes} />
        )}
      </HHAccordion>
      <AddUpdateNoteDialog
        showPciWarning
        id={accountId}
        isOpen={isOpenAddUpdateNoteDialog}
        title={T.NEW_ACCOUNT_NOTE}
        useCreateNoteMutation={useCreateAccountNoteMutation}
        useUpdateNoteMutation={useUpdateAccountNoteMutation}
        onClose={handleCloseAddAccountNote}
      />
      <ViewArchiveNotesDialog
        id={accountId}
        isOpen={isOpenViewArchiveNoteDialog}
        notes={archivedNotes}
        title={T.ARCHIVED_ACCOUNT_NOTES}
        useUnarchiveNoteMutation={usePatchAccountNoteMutation}
        onClose={handleCloseViewArchiveDialog}
      />
    </>
  )
}

export default AccountNotesAccordionVariant
